import { Line3, Matrix4, Plane, Raycaster, Vector3 } from 'three';
import type { BufferAttribute } from 'three';

import { EntityName } from '@frontend/dxfparser';

import { ENTITY_TYPES } from '../constants';
import type { PART_TYPES } from '../constants';
import type { FaceJson } from '../contracts/basic';
import {
  getHorizontalAlignmentVector,
  isAntiParallel,
  isClose,
  isCloseVectors,
  isParallel,
  isPerpendicular,
  isPointOnRay,
} from '../geom-utils';
import { StepEdgeModel } from '../loaders/step/StepEdgeModel';
import { StepFaceModel } from '../loaders/step/StepFaceModel';
import { StepTextModel } from '../loaders/step/StepTextModel';
import type { StepVertexBody } from '../loaders/step/StepVertexBody';
import type { ActiveMetaData } from '../loaders/step/utils';
import type { IntersectionObject } from './intersection-utils';

const PARALLEL = 'parallel' as const;
const ANTIPARALLEL = 'antiparallel' as const;
const PERPENDICULAR = 'perpendicular' as const;
const CONCENTRIC = 'concentric' as const;
const NORMAL_NORMAL = 'normal/normal' as const;
const NORMAL_AXIS = 'normal/axis' as const;
const AXIS_AXIS = 'axis/axis' as const;

const PIDiv2 = Math.PI / 2;

type VectorComparisonInfo = {
  angle: number;
  resolution?:
    | typeof PARALLEL
    | typeof PERPENDICULAR
    | typeof ANTIPARALLEL
    | typeof CONCENTRIC;
};

export type Comparison = Partial<VectorComparisonInfo> & {
  type: typeof NORMAL_NORMAL | typeof NORMAL_AXIS | typeof AXIS_AXIS;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyCaliperObject = CaliperObject<any>;

//CaliperObject: Also see LiteObject which is a JSON encoding of CaliperObject
export type CaliperObject<T extends PART_TYPES> = {
  type: ENTITY_TYPES;
  body: string; //bodyId
  face: (FaceJson & { vertices?: [Vector3, Vector3, Vector3] }) | null;
  obj: IntersectionObject<T>;
  point: Vector3;
};

const getVectorComparisonInfo = (
  v1?: Vector3,
  v2?: Vector3,
): VectorComparisonInfo | Record<string, never> => {
  if (v1 && v2) {
    const angle = v1.angleTo(v2);
    return {
      angle,
      resolution: isClose(angle, 0)
        ? PARALLEL
        : isClose(angle, PIDiv2)
        ? PERPENDICULAR
        : isClose(angle, Math.PI)
        ? ANTIPARALLEL
        : undefined,
    };
  } else {
    return {};
  }
};

export type MeasurementResult = {
  start: Vector3;
  end: Vector3;
  comparison?: Comparison;
  estimated?: boolean;
};

const lineTypes = new Set<string | undefined>(
  [
    //from STEP
    'line',
    //line types from DXF
    EntityName.Line,
    EntityName.MLine,
    EntityName.Polyline,
    EntityName.Lwpolyline,
  ].map((_) => _.toLowerCase()),
);

const getType = ({
  metaData: { type },
}: StepVertexBody | StepEdgeModel | StepFaceModel): string | undefined =>
  //For caliper measurements, the various types of lines must be mapped to 'line'
  lineTypes.has(type) ? 'line' : type;

export const performCaliperMeasurementStep = <T extends PART_TYPES>(
  selections: [CaliperObject<T>, CaliperObject<T>],
): MeasurementResult | undefined => {
  if (selections.some((s) => s.obj instanceof StepTextModel)) {
    return;
  }
  // relies on meta data communication for a lot of things
  // will return the two points to draw the measurement object
  const point1 = selections[0].point;
  const point2 = selections[1].point;
  const face1 = selections[0].face;
  const face2 = selections[1].face;
  //Note: Implementaion doesn't support STL, so faceEdgeOrVertex1 and faceEdgeOrVertex2 are reduced to StepVertexBody | StepEdgeModel | StepFaceModel
  const faceEdgeOrVertex1 = selections[0].obj as
    | StepVertexBody
    | StepEdgeModel
    | StepFaceModel;
  const faceEdgeOrVertex2 = selections[1].obj as
    | StepVertexBody
    | StepEdgeModel
    | StepFaceModel;

  const { axis: axis1, center: center1 } =
    faceEdgeOrVertex1.activeMetaData as Required<ActiveMetaData>;
  const { axis: axis2, center: center2 } =
    faceEdgeOrVertex2.activeMetaData as Required<ActiveMetaData>;
  const class1 = selections[0].type;
  const class2 = selections[1].type;
  const type1 = getType(faceEdgeOrVertex1);
  const type2 = getType(faceEdgeOrVertex2);

  //Note: Always want to clone the normals.
  //* Sometimes face1.normal and face2.normal are NOT Vector3.
  //  * Example: When loading page and a measurement is saved in annotation.
  //* So always constructing Vector3 which has effect of cloning when it is Vector3.
  const normal1 = face1
    ? new Vector3(
        face1.normal.x,
        face1.normal.y,
        face1.normal.z,
      ).transformDirection(faceEdgeOrVertex1.matrixWorld)
    : undefined;
  const normal2 = face2
    ? new Vector3(
        face2.normal.x,
        face2.normal.y,
        face2.normal.z,
      ).transformDirection(faceEdgeOrVertex2.matrixWorld)
    : undefined;

  const plane = new Plane();
  const line = new Line3();
  const rayCaster = new Raycaster();

  // now go through use cases
  // FACE TO FACE
  if (class1 === ENTITY_TYPES.FACE && class2 === ENTITY_TYPES.FACE) {
    // PLANE TO PLANE
    if (type1 === 'plane' && type2 === 'plane') {
      const comparison: Comparison = {
        type: NORMAL_NORMAL,
        ...getVectorComparisonInfo(normal1, normal2),
      };
      // check for three conditions, parallel, anti parallel, and everything between
      if (isAntiParallel(axis1, axis2) || isParallel(axis1, axis2)) {
        // now create the line from the center of plane 1 to where it will intersect with the other plane if its extended
        // so need to redraw the plane given its axis and a point, make it semi infinite, do a ray casting check, and then
        // find the locations to feed the tool

        // get direction of traversal ray by comparing to the normal of the host plane
        const rayDirection = axis1.clone().multiplyScalar(-1).normalize();
        const test = new Vector3().subVectors(point2, point1).dot(rayDirection);
        if (test < 0) {
          rayDirection.multiplyScalar(-1);
        }

        // mathematically with plane and Line3
        const normalDesired = axis1.clone();
        plane.setFromNormalAndCoplanarPoint(normalDesired, point2);
        line.set(
          point1,
          point1.clone().add(rayDirection.clone().multiplyScalar(5000)),
        );
        const intersectionPoint = plane.intersectLine(line, new Vector3());
        if (intersectionPoint) {
          return { start: point1, end: intersectionPoint, comparison };
        } else {
          return { start: point1, end: point2, comparison, estimated: true };
        }
      } else {
        // now for any other planes that dont align to this setup we will just draw lines to eachothers centers
        return { start: point1, end: point2, comparison, estimated: true };
      }
    }
    // PLANE TO CYLINDER OR CYLINDER TO PLANE
    else if (
      (type1 === 'plane' && (type2 === 'cylinder' || type2 === 'cone')) ||
      ((type1 === 'cylinder' || type1 === 'cone') && type2 === 'plane')
    ) {
      // going to want to draw line from the center of the cylinder resolved to the axis of the plane in a
      // direction normal from the cylinder if the two are perpendicular. if they are not draw from the center of
      // the plane to the center of the cylinder
      let pcenter: Vector3,
        ccenter: Vector3,
        paxis: Vector3,
        caxis: Vector3,
        cpoint: Vector3,
        pnormal: Vector3;
      if (type1 === 'plane') {
        pcenter = center1;
        ccenter = center2;
        paxis = axis1;
        caxis = axis2;
        cpoint = point2;
        pnormal = normal1 as Vector3;
      } else {
        pcenter = center2;
        ccenter = center1;
        paxis = axis2;
        caxis = axis1;
        cpoint = point1;
        pnormal = normal2 as Vector3;
      }

      const comparison: Comparison = {
        type: NORMAL_AXIS,
        ...getVectorComparisonInfo(pnormal, caxis),
      };

      if (isPerpendicular(paxis, caxis)) {
        // draw a line from the center of the circle in the direction of the normal and see where it hits at a recreated
        // need to check for the direction of the line by doing a subtraction
        const testDirection = paxis.clone().normalize();
        const test = new Vector3()
          .subVectors(pcenter, ccenter)
          .dot(testDirection);
        const rayDirection =
          test < 0 ? testDirection.clone().negate() : testDirection.clone();
        // infinite plane
        plane.setFromNormalAndCoplanarPoint(
          paxis.clone().normalize(),
          pcenter.clone(),
        );
        const alignmentVector1 = getHorizontalAlignmentVector(caxis, ccenter);
        const al3 = getHorizontalAlignmentVector(caxis, cpoint);
        const newStart = new Vector3().addVectors(
          cpoint,
          new Vector3().subVectors(alignmentVector1, al3),
        );
        line.set(
          newStart,
          newStart.clone().add(rayDirection.multiplyScalar(4000)),
        );
        const intersectionPoint = plane.intersectLine(line, new Vector3());
        if (intersectionPoint) {
          return { start: newStart, end: intersectionPoint, comparison };
        }
      } else {
        // just draw from center to center, can change this if it looks weird
        return { start: point1, end: point2, comparison, estimated: true };
      }
    }
    // CONE TO CONE OR CYLINDER TO CONE
    else if (
      ((type1 === 'cylinder' || type1 === 'cone') &&
        (type2 === 'cylinder' || type2 === 'cone')) ||
      ((type1 === 'cylinder' || type1 === 'cone') &&
        (type2 === 'cylinder' || type2 === 'cone'))
    ) {
      const comparison: Comparison = {
        ...getVectorComparisonInfo(axis1, axis2),
        type: AXIS_AXIS,
      };

      // draw from the host center to the other center if they are parallel
      // check for concentric condition to see if we should do distance from point on one of them to the resolved
      // direction in the normal to the other face, CONCENTRIC IF HAVE SAME ALIGNMENT VECTORS ARE ON TOP OF ONE ANOTHER
      if (isAntiParallel(axis1, axis2) || isParallel(axis1, axis2)) {
        // get the vector aligned between them on the plane of the first center
        // the "traversal" vector
        // need to get their two alignments like from pint and then subtract the vectors to the origin to
        // get the pure traversal vector from one of the centers
        const alignmentVector1 = getHorizontalAlignmentVector(axis1, center1);
        const alignmentVector2 = getHorizontalAlignmentVector(axis2, center2);
        if (
          isCloseVectors(alignmentVector1, alignmentVector2) &&
          type1 === 'cylinder' &&
          type2 === 'cylinder'
        ) {
          const testObject =
            faceEdgeOrVertex2 instanceof StepEdgeModel ||
            faceEdgeOrVertex2 instanceof StepFaceModel
              ? faceEdgeOrVertex2
              : faceEdgeOrVertex2.renderable;
          // they are concentric, and we need to draw a line from one to the other
          rayCaster.set(point1, normal1 as Vector3);
          let intersection = rayCaster.intersectObject(testObject);
          if (!intersection.length) {
            rayCaster.set(
              point1,
              (normal1 as Vector3).clone().multiplyScalar(-1),
            );
            intersection = rayCaster.intersectObject(testObject);
          }
          if (intersection.length) {
            return {
              start: point1,
              end: intersection[0].point,
              comparison: {
                ...comparison,
                resolution: CONCENTRIC,
              },
            };
          }
        }
        // get the vector to pull down the drawn line to the plane of where the face was clicked
        const al3 = getHorizontalAlignmentVector(axis1, point1);
        const newStart = new Vector3().addVectors(
          point1,
          new Vector3().subVectors(alignmentVector1, al3),
        );
        return {
          start: newStart,
          end: newStart
            .clone()
            .add(new Vector3().subVectors(alignmentVector2, alignmentVector1)),
          comparison,
        };
      } else {
        // just draw from center to center
        return { start: point1, end: point2, comparison, estimated: true };
      }
    } else {
      return {
        start: point1,
        end: point2,
        comparison: {
          ...getVectorComparisonInfo(normal1, normal2),
          type: NORMAL_NORMAL,
        },
        estimated: true,
      };
    }
  }
  // FACE TO EDGE, EDGE TO FACE, will be treated the same
  else if (
    (class1 === ENTITY_TYPES.FACE && class2 === ENTITY_TYPES.EDGE) ||
    (class1 === ENTITY_TYPES.EDGE && class2 === ENTITY_TYPES.FACE)
  ) {
    // PLANE TO LINE
    if (
      (type1 === 'plane' && (type2 === 'line' || type2 === 'circle')) ||
      ((type1 === 'line' || type1 === 'circle') && type2 === 'plane')
    ) {
      // see if the line axis is perpendicular to plane, in which case draw the normal towards the line
      let pcenter: Vector3,
        paxis: Vector3,
        laxis: Vector3,
        ltype: string,
        sample: Vector3,
        ppoint: Vector3;
      if (type1 === 'plane') {
        ltype = type2;
        pcenter = center1;
        paxis = axis1;
        laxis = axis2;
        ppoint = point1;
        sample = point2;
      } else {
        ltype = type1;
        pcenter = center2;
        paxis = axis2;
        laxis = axis1;
        ppoint = point2;
        sample = point1;
      }
      const lcenter =
        type1 === 'circle' ? center1 : type2 === 'circle' ? center2 : undefined;

      const comparison: Comparison = {
        ...getVectorComparisonInfo(paxis, laxis),
        type: NORMAL_AXIS,
      };

      if (ltype === 'line') {
        if (isPerpendicular(paxis, laxis)) {
          // create a plane and line and see where they intersect
          // need to figure out which direction to go for the axis
          const test = new Vector3().subVectors(sample, pcenter).dot(paxis);
          const rayDirection =
            test < 0 ? paxis.clone().negate() : paxis.clone();
          plane.setFromNormalAndCoplanarPoint(paxis, sample);
          line.set(
            ppoint,
            ppoint.clone().add(rayDirection.multiplyScalar(4000)),
          );
          const intersectionPoint = plane.intersectLine(line, new Vector3());
          if (intersectionPoint) {
            return {
              start: ppoint,
              end: intersectionPoint,
              comparison,
            };
          }
        } else {
          // just draw from the start of line to the center for now
          return { start: point1, end: point2, comparison, estimated: true };
        }
      } else {
        // now for circles here
        // check if the circle axis is perpendicular to the plane axis/normal
        if (isPerpendicular(paxis, laxis)) {
          // now like with the faces, draw from the circle center to the resolved plane
          // determining proper direction to draw ray
          const testDirection = paxis.clone().normalize();
          const test = new Vector3()
            .subVectors(pcenter, lcenter as Vector3)
            .dot(testDirection);
          const rayDirection =
            test < 0 ? testDirection.clone().negate() : testDirection.clone();
          plane.setFromNormalAndCoplanarPoint(paxis.clone(), pcenter);
          line.set(
            lcenter as Vector3,
            (lcenter as Vector3)
              .clone()
              .add(rayDirection.normalize().multiplyScalar(4000)),
          );
          const intersectionPoint = plane.intersectLine(line, new Vector3());
          if (intersectionPoint) {
            return {
              start: lcenter as Vector3,
              end: intersectionPoint,
              comparison,
            };
          }
        } else {
          // just draw from center to center
          return { start: point1, end: point2, comparison, estimated: true };
        }
      }
    }
    // LINE TO CONE AND CYLINDER
    else if (
      (type1 === 'line' && (type2 === 'cone' || type2 === 'cylinder')) ||
      ((type1 === 'cone' || type1 === 'cylinder') && type2 === 'line')
    ) {
      let ccenter: Vector3,
        caxis: Vector3,
        laxis: Vector3,
        sample: Vector3,
        cpoint: Vector3;
      if (type1 === 'line') {
        laxis = axis1;
        caxis = axis2;
        ccenter = center2;
        cpoint = point1;
        sample = point1;
      } else {
        laxis = axis2;
        caxis = axis1;
        ccenter = center1;
        cpoint = point2;
        sample = point2;
      }

      const comparison: Comparison = {
        ...getVectorComparisonInfo(laxis, caxis),
        type: AXIS_AXIS,
      };

      // check to see if the line axis is perpendicular to circle axis
      if (isPerpendicular(caxis, laxis)) {
        // then draw from the center to the resolved line on the plane of the line
        const testDirection = new Vector3()
          .crossVectors(caxis, laxis)
          .normalize();
        const test = new Vector3()
          .subVectors(ccenter, sample)
          .dot(testDirection);
        const rayDirection =
          test < 0 ? testDirection.clone().negate() : testDirection.clone();
        // infinite plane
        plane.setFromNormalAndCoplanarPoint(rayDirection, sample);
        const alignmentVector1 = getHorizontalAlignmentVector(caxis, ccenter);
        const al3 = getHorizontalAlignmentVector(caxis, cpoint);
        const newStart = new Vector3().addVectors(
          cpoint,
          new Vector3().subVectors(alignmentVector1, al3),
        );
        line.set(
          newStart,
          newStart.clone().add(rayDirection.multiplyScalar(-4000)),
        );
        const intersectionPoint = plane.intersectLine(line, new Vector3());
        if (intersectionPoint) {
          return { start: newStart, end: intersectionPoint, comparison };
        }
      } else {
        // just go from sample to center
        return { start: point1, end: point2, comparison, estimated: true };
      }
    }
    // CIRCLE TO CONE AND CYLINDER
    else if (
      ((type1 === 'cylinder' || type1 === 'cone') && type2 === 'circle') ||
      (type1 === 'circle' && (type2 === 'cylinder' || type2 === 'cone'))
    ) {
      // do the same process here as with two cylinder faces above, go from the center of the edge
      let circleAxis: Vector3,
        circleCenter: Vector3,
        otherAxis: Vector3,
        otherCenter: Vector3;
      if (type1 === 'circle') {
        circleAxis = axis1;
        circleCenter = center1;
        otherAxis = axis2;
        otherCenter = center2;
      } else {
        circleAxis = axis2;
        circleCenter = center2;
        otherAxis = axis1;
        otherCenter = center1;
      }

      const comparison: Comparison = {
        ...getVectorComparisonInfo(circleAxis, otherAxis),
        type: AXIS_AXIS,
      };

      if (isAntiParallel(axis1, axis2) || isParallel(axis1, axis2)) {
        // get the vector aligned between them on the plane of the first center
        // the "traversal" vector
        // need to get their two alignments like from pint and then subtract the vectors to the origin to
        // get the pure traversal vector from one of the centers
        const alignmentVector1 = getHorizontalAlignmentVector(
          circleAxis,
          circleCenter,
        );
        const alignmentVector2 = getHorizontalAlignmentVector(
          otherAxis,
          otherCenter,
        );
        if (isCloseVectors(alignmentVector1, alignmentVector2)) {
          // if they have the same, they are concentric, and we cant create a caliper measurement without
          // two surfaces for the concentric circle condition, so just give the dumb measurement
          return { start: point1, end: point2, comparison };
        }
        return {
          start: circleCenter,
          end: circleCenter
            .clone()
            .add(new Vector3().subVectors(alignmentVector2, alignmentVector1)),
          comparison,
        };
      } else {
        return { start: point1, end: point2, comparison, estimated: true };
      }
    } else {
      return { start: point1, end: point2, estimated: true };
    }
  }
  // EDGE TO EDGE
  else if (class1 === ENTITY_TYPES.EDGE && class2 === ENTITY_TYPES.EDGE) {
    // CIRCLE TO CIRCLE
    if (type1 === 'circle' && type2 === 'circle') {
      const comparison: Comparison = {
        ...getVectorComparisonInfo(axis1, axis2),
        type: AXIS_AXIS,
      };
      if (isAntiParallel(axis1, axis2) || isParallel(axis1, axis2)) {
        // get the vector aligned between them on the plane of the first center
        // the "traversal" vector
        // need to get their two alignments like from pint and then subtract the vectors to the origin to
        // get the pure traversal vector from one of the centers
        const alignmentVector1 = getHorizontalAlignmentVector(axis1, center1);
        const alignmentVector2 = getHorizontalAlignmentVector(axis2, center2);
        if (isCloseVectors(alignmentVector1, alignmentVector2)) {
          // if they have the same, they are concentric, and we cant create a caliper measurement without
          // two surfaces for the concentric circle condition, so just give the dumb measurement
          return {
            start: point1,
            end: point2,
            comparison: {
              ...comparison,
              resolution: CONCENTRIC,
            },
          };
        }
        return {
          start: center1,
          end: center1
            .clone()
            .add(new Vector3().subVectors(alignmentVector2, alignmentVector1)),
          comparison,
        };
      } else {
        return { start: point1, end: point2, comparison, estimated: true };
      }
    }
    // LINE TO LINE
    else if (type1 === 'line' && type2 === 'line') {
      const comparison: Comparison = {
        ...getVectorComparisonInfo(axis1, axis2),
        type: AXIS_AXIS,
      };
      if (isAntiParallel(axis1, axis2) || isParallel(axis1, axis2)) {
        // means they are co-planar, treat it like a rotated alignment
        const alignmentVector1 = getHorizontalAlignmentVector(axis1, point1);
        const alignmentVector2 = getHorizontalAlignmentVector(axis2, point2);
        return {
          start: point1,
          end: point1
            .clone()
            .add(new Vector3().subVectors(alignmentVector2, alignmentVector1)),
          comparison,
        };
      } else {
        return { start: point1, end: point2, comparison, estimated: true };
      }
    }
    // LINE TO CIRCLE OR CIRCLE TO LINE
    else if (
      (type1 === 'line' && type2 === 'circle') ||
      (type1 === 'circle' && type2 === 'line')
    ) {
      const comparison: Comparison = {
        ...getVectorComparisonInfo(axis1, axis2),
        type: AXIS_AXIS,
      };

      let ccenter: Vector3, laxis: Vector3, caxis: Vector3, sample: Vector3;
      if (type1 === 'line') {
        ccenter = center2;
        laxis = axis1;
        caxis = axis2;
        sample = point1;
      } else {
        ccenter = center1;
        laxis = axis2;
        caxis = axis1;
        sample = point2;
      }
      // check to see if the line axis is perpendicular to circle axis
      if (isPerpendicular(caxis, laxis)) {
        // then draw from the center to the resolved line on the plane of the line
        const testDirection = new Vector3()
          .crossVectors(caxis, laxis)
          .normalize();
        const test = new Vector3()
          .subVectors(ccenter, sample)
          .dot(testDirection);
        const rayDirection =
          test < 0 ? testDirection.clone().negate() : testDirection.clone();
        // infinite plane
        plane.setFromNormalAndCoplanarPoint(rayDirection, sample);
        line.set(
          ccenter,
          ccenter.clone().add(rayDirection.multiplyScalar(-4000)),
        );
        const intersectionPoint = plane.intersectLine(line, new Vector3());
        if (intersectionPoint) {
          return { start: ccenter, end: intersectionPoint, comparison };
        }
      } else {
        return { start: point1, end: point2, comparison, estimated: true };
      }
    } else {
      return { start: point1, end: point2, estimated: true };
    }
  }
  // VERTEX TO VERTEX
  else if (class1 === ENTITY_TYPES.VERTEX && class2 === ENTITY_TYPES.VERTEX) {
    return { start: center1, end: center2 };
  }
  // VERTEX TO FACE
  else if (
    (class1 === ENTITY_TYPES.VERTEX && class2 === ENTITY_TYPES.FACE) ||
    (class1 === ENTITY_TYPES.FACE && class2 === ENTITY_TYPES.VERTEX)
  ) {
    // PLANE TO VERTEX
    if (type1 === 'plane' || type2 === 'plane') {
      let pobj: StepFaceModel, pcenter: Vector3, vcenter: Vector3;
      if (type1 === 'plane') {
        pobj = faceEdgeOrVertex1 as StepFaceModel;
        pcenter = center1;
        vcenter = center2;
      } else {
        pobj = faceEdgeOrVertex2 as StepFaceModel;
        pcenter = center2;
        vcenter = center1;
      }
      // now make a semi infinite plane from the other point and draw a ray from one another to intersect them
      const testDirection = new Vector3().subVectors(pcenter, vcenter);
      const rayDirection = pobj.firstNormal; //For a plane, the normal in the face is fine, so using the first

      const rotMatrix = new Matrix4().extractRotation(pobj.transform);
      rayDirection.applyMatrix4(rotMatrix);
      const test = testDirection.dot(rayDirection);
      // if its close to to zero, that means they are coplanar, so return just center to center
      if (isClose(test, 0)) {
        return { start: pcenter, end: vcenter };
      }
      // if not coplanar check which direction to draw the ray
      if (test < 0) {
        rayDirection.multiplyScalar(-1);
      }
      plane.setFromNormalAndCoplanarPoint(rayDirection, pcenter);
      line.set(vcenter, vcenter.clone().add(rayDirection.multiplyScalar(4000)));
      const intersectionPoint = plane.intersectLine(line, new Vector3());
      if (intersectionPoint) {
        return { start: vcenter, end: intersectionPoint };
      }
    }
    // VERTEX TO CYLINDER OR CONICAL FACE
    else if (
      type1 === 'cylinder' ||
      type2 === 'cylinder' ||
      type1 === 'cone' ||
      type2 === 'cone'
    ) {
      let ccenter: Vector3, caxis: Vector3, vcenter: Vector3;
      if (type1 === 'cylinder' || type1 === 'cone') {
        ccenter = center1;
        caxis = axis1;
        vcenter = center2;
      } else {
        ccenter = center2;
        caxis = axis2;
        vcenter = center1;
      }
      // now make an extended plane based on the axis and draw the vector to the alignments
      const alignmentVector1 = getHorizontalAlignmentVector(caxis, ccenter);
      const alignmentVector2 = getHorizontalAlignmentVector(caxis, vcenter);
      const traversal = new Vector3().subVectors(
        alignmentVector1,
        alignmentVector2,
      );
      return { start: vcenter, end: vcenter.clone().add(traversal) };
    }
    // VERTEX TO OTHER TYPE OF FACE
    else {
      let vcenter: Vector3, otherPoint: Vector3;
      if (class1 === ENTITY_TYPES.VERTEX) {
        vcenter = center1;
        otherPoint = point2;
      } else {
        vcenter = center2;
        otherPoint = point1;
      }
      return { start: vcenter, end: otherPoint, estimated: true };
    }
  }
  // VERTEX TO EDGE
  else if (
    (class1 === ENTITY_TYPES.VERTEX && class2 === ENTITY_TYPES.EDGE) ||
    (class1 === ENTITY_TYPES.EDGE && class2 === ENTITY_TYPES.VERTEX)
  ) {
    // VERTEX TO CIRCLE
    if (type1 === 'circle' || type2 === 'circle') {
      let ccenter: Vector3, caxis: Vector3, vcenter: Vector3;
      if (type1 === 'circle') {
        ccenter = center1;
        caxis = axis1;
        vcenter = center2;
      } else {
        ccenter = center2;
        caxis = axis2;
        vcenter = center1;
      }
      // now make an extended plane based on the axis and draw the vector to the alignments
      const alignmentVector1 = getHorizontalAlignmentVector(caxis, ccenter);
      const alignmentVector2 = getHorizontalAlignmentVector(caxis, vcenter);
      const traversal = new Vector3().subVectors(
        alignmentVector1,
        alignmentVector2,
      );
      return { start: vcenter, end: vcenter.clone().add(traversal) };
    }
    // VERTEX TO LINE
    else if (type1 === 'line' || type2 === 'line') {
      let lobj: StepVertexBody | StepEdgeModel | StepFaceModel,
        laxis: Vector3,
        vcenter: Vector3;
      if (type1 === 'line') {
        lobj = faceEdgeOrVertex1 as
          | StepVertexBody
          | StepEdgeModel
          | StepFaceModel;
        laxis = axis1;
        vcenter = center2;
      } else {
        lobj = faceEdgeOrVertex2 as
          | StepVertexBody
          | StepEdgeModel
          | StepFaceModel;
        laxis = axis2;
        vcenter = center1;
      }
      // resolve a plane by crossing vector to v and laxis and normalizing it
      // then get the perpendicular direction to v from these two vectors
      // then resolve distance from v to l in this perpendicular direction
      const p1 =
        lobj instanceof StepEdgeModel || lobj instanceof StepFaceModel
          ? lobj.firstPosition
          : new Vector3().fromBufferAttribute(
              lobj.geometry.geometry.attributes.position as BufferAttribute,
              0,
            );

      p1.applyMatrix4(lobj.transform);
      const traversal1 = new Vector3().subVectors(vcenter, p1);
      const normal = new Vector3().crossVectors(laxis, traversal1).normalize();
      // need to check for colinearity. if so, just return nothing
      const ray = { direction: laxis, startPoint: p1 };
      if (isPointOnRay(ray, vcenter)) {
        return;
      }
      const traversalDirection = new Vector3()
        .crossVectors(normal, laxis)
        .normalize();
      // now calculate the intersection with the plane of the line
      plane.setFromNormalAndCoplanarPoint(traversalDirection, p1);
      line.set(
        vcenter,
        vcenter.clone().add(traversalDirection.clone().multiplyScalar(-4000)),
      );
      const intersectPoint = plane.intersectLine(line, new Vector3());
      if (intersectPoint) {
        return { start: vcenter, end: intersectPoint };
      }
    }
    // VERTEX TO OTHER TYPE OF LINE WONT RETURN ANYTHING
    else {
      let vcenter: Vector3, otherPoint: Vector3;
      if (class1 === ENTITY_TYPES.VERTEX) {
        vcenter = center1;
        otherPoint = point2;
      } else {
        vcenter = center2;
        otherPoint = point1;
      }
      return { start: vcenter, end: otherPoint, estimated: true };
    }
  }
};

export const performCaliperMeasurementStl = <T extends PART_TYPES>(
  selections: CaliperObject<T>[],
): MeasurementResult => {
  const point1 = selections[0].point;
  const point2 = selections[1].point;
  const face1 = selections[0].face;
  const face2 = selections[1].face;
  if (
    selections[0].type === ENTITY_TYPES.VERTEX ||
    selections[1].type === ENTITY_TYPES.VERTEX
  ) {
    return { start: point1, end: point2 };
  }
  if (!face1 || !face2) {
    return { start: point1, end: point2 };
  }
  //Note: Implementaion doesn't support STL, so faceEdgeOrVertex1 and faceEdgeOrVertex2 are reduced to StepVertexBody | StepEdgeModel | StepFaceModel
  const faceEdgeOrVertex1 = selections[0].obj as
    | StepVertexBody
    | StepEdgeModel
    | StepFaceModel;
  const faceEdgeOrVertex2 = selections[1].obj as
    | StepVertexBody
    | StepEdgeModel
    | StepFaceModel;

  //Note: Always want to clone the normals.
  //* Sometimes face1.normal and face2.normal are NOT Vector3.
  //  * Example: When loading page and a measurement is saved in annotation.
  //* So always constructing Vector3 which has effect of cloning when it is Vector3.
  const normal1 = face1
    ? new Vector3(
        face1.normal.x,
        face1.normal.y,
        face1.normal.z,
      ).transformDirection(faceEdgeOrVertex1.matrixWorld)
    : undefined;
  const normal2 = face2
    ? new Vector3(
        face2.normal.x,
        face2.normal.y,
        face2.normal.z,
      ).transformDirection(faceEdgeOrVertex2.matrixWorld)
    : undefined;

  const comparison: Comparison = {
    ...getVectorComparisonInfo(normal1, normal2),
    type: NORMAL_NORMAL,
  };

  const plane = new Plane();
  const line = new Line3();

  if (isAntiParallel(normal1, normal2) || isParallel(normal1, normal2)) {
    // get direction of traversal ray by comparing to the normal of the host plane
    const rayDirection = (normal1 as Vector3)
      .clone()
      .multiplyScalar(-1)
      .normalize();
    const test = new Vector3().subVectors(point2, point1).dot(rayDirection);
    if (test < 0) {
      rayDirection.multiplyScalar(-1);
    }

    // mathematically with plane and Line3
    const normalDesired = (normal1 as Vector3).clone();
    plane.setFromNormalAndCoplanarPoint(normalDesired, point2);
    line.set(
      point1,
      point1.clone().add(rayDirection.clone().multiplyScalar(5000)),
    );
    const intersectionPoint = plane.intersectLine(line, new Vector3());
    if (intersectionPoint) {
      return { start: point1, end: intersectionPoint, comparison };
    } else {
      return { start: point1, end: point2, comparison, estimated: true };
    }
  } else {
    return { start: point1, end: point2, comparison, estimated: true };
  }
};
